/**
 * Catálogo declarativo y **versionado** de Acciones_Semanticas de la
 * Capa Operacional AI-First (Registro_Acciones_Semanticas, R12.2).
 *
 * Este módulo contiene únicamente **datos** (no lógica): la lista de
 * {@link AccionDeclarada} que el Copilot, un Insight o el Motor_Casos
 * pueden invocar a través del {@link "./registry"}. Mantenerlo separado
 * de `registry.ts` permite versionar el catálogo de forma explícita y
 * auditable, y poblarlo desde un JSON real en producción sin tocar la
 * lógica del Registro.
 *
 * Cada Accion_Semantica se declara con (R12.2):
 *   - `parametros`        — contrato de invocación;
 *   - `permisos_requeridos` — roles RBAC de la fundación que la autorizan;
 *   - `reversible` + `operacion_inversa` — política de reversibilidad
 *     (R12.6, R20.2);
 *   - `criticidad`        — clasificación baja/media/alta;
 *   - `es_critica_hidraulica` — marca explícita de Accion_Critica_
 *     Hidraulica (R18.1).
 *
 * Reglas de seguridad reflejadas en el catálogo:
 *   - **R18.1** — apertura/cierre de Valvula, cambio de setpoint de PRV
 *     y eliminación de evidencia se declaran con
 *     `es_critica_hidraulica: true`.
 *   - **R20.3** — la *clasificación efectiva* como crítica de toda acción
 *     no reversible o de criticidad elevada la realiza el Registro
 *     (`../lib/registry`), no este catálogo, para no acoplar los datos a
 *     la regla derivada.
 *
 * Los `id` de las acciones de inspección/operación coinciden con los que
 * el Motor_Casos referencia en `suggestActions` (`./motorCasos`), de modo
 * que toda acción sugerida resuelve contra una declaración del catálogo
 * (R4.7).
 *
 * Convención de reversibilidad usada en el catálogo demo:
 *   - acción que **muta** estado y puede deshacerse → `reversible: true`
 *     con `operacion_inversa` apuntando a la acción inversa declarada;
 *   - acción de **lectura/notificación** sin efecto físico que deshacer →
 *     `reversible: true` con `operacion_inversa: null` (no-op a revertir);
 *   - acción **destructiva irreversible** (eliminar evidencia) →
 *     `reversible: false` con `operacion_inversa: null`.
 */
import type { AccionDeclarada } from "../api/contracts";

/**
 * Versión semántica del catálogo. Se incrementa ante cualquier cambio en
 * la declaración de acciones para que la Observabilidad y la auditoría
 * puedan correlacionar una invocación con la versión vigente del Registro.
 */
export const CATALOGO_VERSION = "1.0.0";

// ---------------------------------------------------------------------------
// Identificadores estables de Acciones_Semanticas
// ---------------------------------------------------------------------------
// Se exportan como constantes para evitar literales mágicos y para que los
// consumidores (Motor_Casos, Copilot, tests) los referencien por nombre.

/** Abrir una Orden_Inspeccion de la fundación (creación de orden). */
export const ACCION_ABRIR_ORDEN_INSPECCION = "abrir_orden_inspeccion";
/** Programar una inspección futura. */
export const ACCION_PROGRAMAR_INSPECCION = "programar_inspeccion";
/** Cancelar una Orden_Inspeccion (inversa de abrir/programar). */
export const ACCION_CANCELAR_ORDEN_INSPECCION = "cancelar_orden_inspeccion";
/** Abrir una Valvula (Accion_Critica_Hidraulica, R18.1). */
export const ACCION_ABRIR_VALVULA = "abrir_valvula";
/** Cerrar una Valvula (Accion_Critica_Hidraulica, R18.1). */
export const ACCION_CERRAR_VALVULA = "cerrar_valvula";
/** Ajustar el setpoint de una PRV (Accion_Critica_Hidraulica, R18.1). */
export const ACCION_AJUSTAR_SETPOINT_PRV = "ajustar_setpoint_prv";
/** Asignar una Cuadrilla a una Orden_Inspeccion. */
export const ACCION_ASIGNAR_CUADRILLA = "asignar_cuadrilla";
/** Desasignar una Cuadrilla (inversa de asignar). */
export const ACCION_DESASIGNAR_CUADRILLA = "desasignar_cuadrilla";
/** Notificar a una Cuadrilla (mensaje operacional). */
export const ACCION_NOTIFICAR_CUADRILLA = "notificar_cuadrilla";
/** Revisar la Cobertura_RF de un dispositivo (diagnóstico, sin mutación). */
export const ACCION_REVISAR_RF = "revisar_cobertura_rf";
/** Eliminar evidencia (Accion_Critica_Hidraulica irreversible, R18.1). */
export const ACCION_ELIMINAR_EVIDENCIA = "eliminar_evidencia";

// ---------------------------------------------------------------------------
// Catálogo declarativo
// ---------------------------------------------------------------------------

/**
 * Catálogo base de Acciones_Semanticas. Se expone `readonly` para que
 * el Registro lo cargue por copia profunda y garantice el round-trip
 * exacto sin aliasing (Property 30, R12.2).
 */
export const CATALOGO_ACCIONES: readonly AccionDeclarada[] = [
  // --- Operaciones de inspección / órdenes (no hidráulicas) ----------------
  {
    id: ACCION_ABRIR_ORDEN_INSPECCION,
    nombre: "Abrir Orden_Inspeccion",
    parametros: [
      { nombre: "dma_ref", tipo: "ref", requerido: true },
      { nombre: "prioridad", tipo: "number", requerido: false },
      { nombre: "descripcion", tipo: "string", requerido: false },
    ],
    permisos_requeridos: ["Supervisor", "Despachador"],
    reversible: true,
    operacion_inversa: ACCION_CANCELAR_ORDEN_INSPECCION,
    criticidad: "media",
    es_critica_hidraulica: false,
  },
  {
    id: ACCION_PROGRAMAR_INSPECCION,
    nombre: "Programar inspección",
    parametros: [
      { nombre: "dma_ref", tipo: "ref", requerido: true },
      { nombre: "fecha", tipo: "string", requerido: true },
    ],
    permisos_requeridos: ["Supervisor", "Despachador", "Analista_NRW"],
    reversible: true,
    operacion_inversa: ACCION_CANCELAR_ORDEN_INSPECCION,
    criticidad: "baja",
    es_critica_hidraulica: false,
  },
  {
    id: ACCION_CANCELAR_ORDEN_INSPECCION,
    nombre: "Cancelar Orden_Inspeccion",
    parametros: [{ nombre: "orden_ref", tipo: "ref", requerido: true }],
    permisos_requeridos: ["Supervisor", "Despachador"],
    reversible: true,
    operacion_inversa: ACCION_ABRIR_ORDEN_INSPECCION,
    criticidad: "baja",
    es_critica_hidraulica: false,
  },

  // --- Gestión de cuadrillas (no hidráulicas) ------------------------------
  {
    id: ACCION_ASIGNAR_CUADRILLA,
    nombre: "Asignar Cuadrilla",
    parametros: [
      { nombre: "cuadrilla_ref", tipo: "ref", requerido: true },
      { nombre: "orden_ref", tipo: "ref", requerido: true },
    ],
    permisos_requeridos: ["Despachador", "Supervisor"],
    reversible: true,
    operacion_inversa: ACCION_DESASIGNAR_CUADRILLA,
    criticidad: "media",
    es_critica_hidraulica: false,
  },
  {
    id: ACCION_DESASIGNAR_CUADRILLA,
    nombre: "Desasignar Cuadrilla",
    parametros: [
      { nombre: "cuadrilla_ref", tipo: "ref", requerido: true },
      { nombre: "orden_ref", tipo: "ref", requerido: true },
    ],
    permisos_requeridos: ["Despachador", "Supervisor"],
    reversible: true,
    operacion_inversa: ACCION_ASIGNAR_CUADRILLA,
    criticidad: "baja",
    es_critica_hidraulica: false,
  },
  {
    id: ACCION_NOTIFICAR_CUADRILLA,
    nombre: "Notificar Cuadrilla",
    parametros: [
      { nombre: "cuadrilla_ref", tipo: "ref", requerido: true },
      { nombre: "mensaje", tipo: "string", requerido: true },
    ],
    permisos_requeridos: ["Despachador", "Supervisor", "Operador"],
    // Notificación sin efecto físico que deshacer: reversible no-op.
    reversible: true,
    operacion_inversa: null,
    criticidad: "baja",
    es_critica_hidraulica: false,
  },

  // --- Diagnóstico / lectura (sin mutación) --------------------------------
  {
    id: ACCION_REVISAR_RF,
    nombre: "Revisar Cobertura_RF",
    parametros: [{ nombre: "device_ref", tipo: "ref", requerido: true }],
    permisos_requeridos: ["Operador", "Analista_NRW", "Supervisor"],
    // Diagnóstico de solo lectura: nada que revertir.
    reversible: true,
    operacion_inversa: null,
    criticidad: "baja",
    es_critica_hidraulica: false,
  },

  // --- Acciones_Critica_Hidraulica (R18.1) ---------------------------------
  {
    id: ACCION_ABRIR_VALVULA,
    nombre: "Abrir Valvula",
    parametros: [{ nombre: "valvula_ref", tipo: "ref", requerido: true }],
    permisos_requeridos: ["Operador", "Supervisor"],
    reversible: true,
    operacion_inversa: ACCION_CERRAR_VALVULA,
    criticidad: "alta",
    es_critica_hidraulica: true,
  },
  {
    id: ACCION_CERRAR_VALVULA,
    nombre: "Cerrar Valvula",
    parametros: [{ nombre: "valvula_ref", tipo: "ref", requerido: true }],
    permisos_requeridos: ["Operador", "Supervisor"],
    reversible: true,
    operacion_inversa: ACCION_ABRIR_VALVULA,
    criticidad: "alta",
    es_critica_hidraulica: true,
  },
  {
    id: ACCION_AJUSTAR_SETPOINT_PRV,
    nombre: "Ajustar setpoint de PRV",
    parametros: [
      { nombre: "prv_ref", tipo: "ref", requerido: true },
      { nombre: "setpoint", tipo: "number", requerido: true },
    ],
    permisos_requeridos: ["Supervisor"],
    // El setpoint se restituye reajustando al valor previo (auto-inversa).
    reversible: true,
    operacion_inversa: ACCION_AJUSTAR_SETPOINT_PRV,
    criticidad: "alta",
    es_critica_hidraulica: true,
  },
  {
    id: ACCION_ELIMINAR_EVIDENCIA,
    nombre: "Eliminar evidencia",
    parametros: [{ nombre: "evidencia_ref", tipo: "ref", requerido: true }],
    permisos_requeridos: ["Supervisor"],
    // Destrucción de evidencia: irreversible y crítica por R18.1.
    reversible: false,
    operacion_inversa: null,
    criticidad: "alta",
    es_critica_hidraulica: true,
  },
];
